import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { HashLink } from 'react-router-hash-link';
import { motion, AnimatePresence } from 'framer-motion';
import '../styles/Footer.css';

// --- DATA ---
const socialLinks = [
  { name: "Twitter", href: "https://twitter.com/RobertFalkenbe6" },
  { name: "Instagram", href: "https://www.instagram.com/robertfalkenbergauthor/" },
  { name: "Facebook", href: "https://www.facebook.com/profile.php?id=100086048815176" },
];
const siteLinks = [
    { name: "Home", to: "/#home" },
    { name: "About", to: "/#about" },
    { name: "Books", to: "/#books" },
    { name: "Reviews", to: "/#reviews" },
    { name: "Newsletter", to: "/#newsletter" },
    { name: "Blog", to: "/standing-next-to-a-mountain" },
    { name: "Speaker", to: "/speaker"}
];
const legalLinks = [
    { name: "Privacy Policy", to: "/privacy" },
    { name: "Terms of Use", to: "/terms" },
];
const authorEmail = "robertfalkenbergauthor@gmail.com";

const MAILCHIMP_POST_URL = process.env.REACT_APP_MAILCHIMP_URL;

// --- ANIMATION VARIANTS ---
const footerVariants = {
  hidden: { opacity: 0, y: 80 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
        duration: 0.9,
        ease: [0.16, 1, 0.3, 1],
        staggerChildren: 0.1
    },
  },
};

const childVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] }
  },
};

// --- MAIN COMPONENT ---
export default function Footer() {
  const [status, setStatus] = useState('idle');
  const [message, setMessage] = useState('');
  const [navHeight, setNavHeight] = useState(0);

  useEffect(() => {
    const navElement = document.querySelector('.local-navbar-container');
    if (navElement) {
      setNavHeight(navElement.offsetHeight);
    }
  }, []);

  const scrollWithOffset = (el) => {
    const yCoordinate = el.getBoundingClientRect().top + window.pageYOffset;
    const yOffset = -navHeight; 
    window.scrollTo({ top: yCoordinate + yOffset, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setMessage('');

    const formData = new FormData(e.target);
    const url = `${MAILCHIMP_POST_URL}&c=?`;

    try {
      await fetch(url, {
        method: 'POST',
        body: formData,
        mode: 'no-cors',
      });
      setStatus('success');
      setMessage("Thank you for subscribing! Please check your email to confirm.");
      e.target.reset();
    } catch (error) {
      console.error("Mailchimp submission error:", error);
      setStatus('error');
      setMessage('Something went wrong. Please try again.');
    } finally {
        setTimeout(() => {
            if (status !== 'loading') {
                setStatus('idle');
                setMessage('');
            }
        }, 5000);
    }
  };

  return (
    <motion.footer
        id="footer"
        className="site-footer"
        variants={footerVariants}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.2 }}
    >
      <div className="footer-container">
        <div className="footer-main">
          <motion.div className="footer-cta-column" variants={childVariants}>
            <h2 className="footer-heading">Stay Connected</h2>
            <p className="footer-subheading">
              Join the newsletter to stay updated on new releases, exclusive content, and author insights.
            </p>
            
            <form className="footer-newsletter-form" onSubmit={handleSubmit}>
              <div className="form-row-names">
                <input type="text" name="FNAME" placeholder="First Name" required />
                <input type="text" name="LNAME" placeholder="Last Name" required />
              </div>
              <div className="form-row-email">
                <input type="email" name="EMAIL" placeholder="Email Address *" required />
              </div>
              <motion.button 
                type="submit"
                className="submit-btn"
                disabled={status === 'loading'}
                whileHover={{ y: -2 }}
                whileTap={{ y: 1 }}
              >
                {status === 'loading' ? 'Submitting…' : 'Subscribe'}
              </motion.button>
              
              <AnimatePresence>
                {message && (
                  <motion.p
                    className={`form-message ${status}`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.3 }}
                  >
                    {message}
                  </motion.p>
                )}
              </AnimatePresence>
            </form>
          </motion.div>

          <motion.div className="footer-links-column" variants={childVariants}>
            <div className="footer-links-group">
                <h4 className="footer-links-title">Navigate</h4>
                {siteLinks.map(link => {
                  if (!link.to.startsWith('/#')) {
                    return (
                      <Link key={link.name} to={link.to} className="footer-link">
                        {link.name}
                      </Link>
                    );
                  }
                  return (
                    <HashLink
                      key={link.name}
                      smooth
                      to={link.to}
                      className="footer-link"
                      scroll={scrollWithOffset}
                    >
                      {link.name}
                    </HashLink>
                  );
                })}
            </div>
            <div className="footer-links-group">
                <h4 className="footer-links-title">Social</h4>
                {socialLinks.map((link) => (
                    <a key={link.name} href={link.href} target="_blank" rel="noopener noreferrer" className="footer-link">
                      {link.name}
                    </a>
                ))}
            </div>
            
            <div className="footer-links-group">
                <h4 className="footer-links-title">Contact</h4>
                <a href={`mailto:${authorEmail}`} className="footer-link">
                  {authorEmail}
                </a>
            </div>

            <div className="footer-links-group">
                <h4 className="footer-links-title">Legal</h4>
                {legalLinks.map(link => (
                    <Link key={link.name} to={link.to} className="footer-link">{link.name}</Link>
                ))}
            </div>
          </motion.div>
        </div>

        <motion.div className="footer-bottom" variants={childVariants}>
            <p className='footer-copyright'>
                © {new Date().getFullYear()} Robert Falkenberg. All Rights Reserved.
            </p>
            <HashLink
              smooth
              to="/#"
              className="footer-scroll-top"
              scroll={scrollWithOffset}
            >
              Scroll to Top
            </HashLink>
            <a href="https://loganfalkenberg.com/" target="_blank" rel="noopener noreferrer" className="footer-credit">
                Website developed by Logan Falkenberg
            </a>
        </motion.div>
      </div>
    </motion.footer>
  );
}